import fetch from 'cross-fetch';

export interface ApiSpecOperation {
  microserviceId?: string;
  baseUrl?: string;
  path?: string;
  method?: string;
  params?: any;
}

export interface ApiSpec {
  retrieval: ApiSpecOperation;
  validation?: ApiSpecOperation;
}

export interface FetchMicroserviceOptions {
  backendBaseUrl: string;
  operation: 'retrieval' | 'validation';
  apiSpec: ApiSpec;
  page?: number;
  limit?: number;
  filter?: string;
  /** When true and the call is external (not CURRENT_HOST), `page` is forwarded as-is as the offset
   *  (some legacy microservices may use page numbers directly as offset). For CURRENT_HOST calls this flag is always ignored —
   *  internal microservices always receive an actual item offset (`page * limit`).
   */
  oldOffsetBehaviour?: boolean;
  /**
   * When true, a `hasMore` field is included in the result indicating whether additional
   * items exist beyond the current page. `data` will be a normalised items array (`any[]`).
   * Only meaningful for retrieval operations.
   */
  checkHasMore?: boolean;
  body?: any;
  token?: string;
}

export interface FetchMicroserviceResult {
  /** Whether the response was successful (HTTP 2xx). */
  ok: boolean;
  /** HTTP status code. */
  status: number;
  /**
   * Parsed response body.
   * - Retrieval operations: always `any[]` per the Custom URL Picker API spec.
   * - Validation operations: the raw parsed JSON object (or `null` if parsing failed).
   */
  data: any;
  /** Only set when `checkHasMore: true` was passed. `true` if more items exist beyond the current page. */
  hasMore?: boolean;
}

const DEFAULT_RETRIEVAL_LIMIT = 5;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Dispatches one HTTP request — through the backend proxy for external calls,
 * or directly to the configured path for CURRENT_HOST calls — using a pre-computed
 * queryParams string.
 */
function dispatch(
  isCurrentHost: boolean,
  operationSpec: ApiSpecOperation,
  backendBaseUrl: string,
  operation: string,
  apiSpec: ApiSpec,
  queryParams: string | undefined,
  body: any,
  token: string | undefined,
): Promise<Response> {
  if (!isCurrentHost) {
    // Route ALL external calls through backend proxy — backend handles config resolution
    // (when microserviceId is present) or forwards directly (when absent)
    return fetch(`${backendBaseUrl}/api/builder/microservice-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ operation, apiSpec, queryParams, body }),
    });
  }

  // Direct fetch for {{CURRENT_HOST}}
  const path = operationSpec?.path ?? '/v1/resources';
  const method = operationSpec?.method ?? 'POST';
  const fullPath = queryParams ? `${path}?${queryParams}` : path;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${backendBaseUrl}/api${fullPath}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

/** Safely parses the JSON body of a response, returning `null` on failure. */
async function parseJson(response: Response): Promise<any> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** Returns the items array from a parsed retrieval response, or `[]` if parsing failed. */
function extractItems(data: any): any[] {
  return Array.isArray(data) ? data : [];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Routes microservice calls through the backend proxy for external APIs,
 * or performs a direct fetch for {{CURRENT_HOST}} calls.
 *
 * Always returns a {@link FetchMicroserviceResult} with the parsed response body.
 * Pass `checkHasMore: true` to also receive a `hasMore` flag indicating whether
 * additional items exist beyond the current page; `data` will then be a normalised
 * items array.
 */
export async function fetchMicroservice(
  options: FetchMicroserviceOptions,
): Promise<FetchMicroserviceResult> {
  const {
    backendBaseUrl,
    operation,
    apiSpec,
    page,
    limit,
    filter,
    oldOffsetBehaviour,
    checkHasMore,
    body,
    token,
  } = options;

  const operationSpec = apiSpec[operation] ?? apiSpec.retrieval;
  const baseUrl = operationSpec?.baseUrl ?? '';
  const isCurrentHost = baseUrl === '{{CURRENT_HOST}}';
  const effectiveLimit = limit ?? DEFAULT_RETRIEVAL_LIMIT;
  // oldOffsetBehaviour is ignored for CURRENT_HOST — internal APIs always expect real offsets
  const useOldBehaviour = (oldOffsetBehaviour ?? false) && !isCurrentHost;

  // --------------------------------------------------------------------------
  // checkHasMore path — retrieval only
  // --------------------------------------------------------------------------
  if (checkHasMore && operation === 'retrieval') {
    // If page is omitted the caller implicitly means the first page.
    const effectivePage = page ?? 0;

    if (!useOldBehaviour) {
      // Standard mode (including CURRENT_HOST): offset = page * limit.
      // Requesting limit+1 items is enough to know whether a next page exists —
      // offset uses the original limit so no items are skipped at page boundaries.
      const sp = new URLSearchParams();
      sp.set('offset', String(effectivePage * effectiveLimit));
      sp.set('limit', String(effectiveLimit + 1));
      if (filter) sp.set('filter', filter);

      const resp = await dispatch(
        isCurrentHost,
        operationSpec,
        backendBaseUrl,
        operation,
        apiSpec,
        sp.toString(),
        body,
        token,
      );
      const rawData = await parseJson(resp);
      const allItems = extractItems(rawData);
      const hasMore = allItems.length > effectiveLimit;
      const data: any[] = hasMore
        ? allItems.slice(0, effectiveLimit)
        : allItems;

      return { ok: resp.ok, status: resp.status, data, hasMore };
    }

    // oldOffsetBehaviour (external APIs only): `page` is forwarded as a raw offset,
    // so requesting limit+1 would still target the same logical page. A separate
    // request at offset+1 is needed to determine whether the next page exists.
    // The probe uses offset=(page+1)*effectiveLimit with limit=1: since the API
    // computes start = offset * limit, with limit=1 this becomes start=(page+1)*effectiveLimit,
    // which is the first item of the next page — the same formula as standard mode.
    const mainSp = new URLSearchParams();
    mainSp.set('offset', String(effectivePage));
    mainSp.set('limit', String(effectiveLimit));
    if (filter) mainSp.set('filter', filter);

    const probeSp = new URLSearchParams();
    probeSp.set('offset', String((effectivePage + 1) * effectiveLimit));
    probeSp.set('limit', '1');
    if (filter) probeSp.set('filter', filter);

    const [mainResp, probeResp] = await Promise.all([
      dispatch(
        isCurrentHost,
        operationSpec,
        backendBaseUrl,
        operation,
        apiSpec,
        mainSp.toString(),
        body,
        token,
      ),
      dispatch(
        isCurrentHost,
        operationSpec,
        backendBaseUrl,
        operation,
        apiSpec,
        probeSp.toString(),
        body,
        token,
      ),
    ]);
    const [mainData, probeData] = await Promise.all([
      parseJson(mainResp),
      parseJson(probeResp),
    ]);

    return {
      ok: mainResp.ok,
      status: mainResp.status,
      data: extractItems(mainData),
      hasMore: extractItems(probeData).length > 0,
    };
  }

  // --------------------------------------------------------------------------
  // Normal path — build queryParams and return parsed response
  // --------------------------------------------------------------------------
  let queryParams: string | undefined;
  if (
    operation === 'retrieval' &&
    (page !== undefined || limit !== undefined || filter !== undefined)
  ) {
    const searchParams = new URLSearchParams();
    if (page !== undefined) {
      const offset = useOldBehaviour ? page : page * effectiveLimit;
      searchParams.set('offset', String(offset));
    }
    searchParams.set('limit', String(effectiveLimit));
    if (filter) searchParams.set('filter', filter);
    queryParams = searchParams.toString();
  }

  const response = await dispatch(
    isCurrentHost,
    operationSpec,
    backendBaseUrl,
    operation,
    apiSpec,
    queryParams,
    body,
    token,
  );
  return {
    ok: response.ok,
    status: response.status,
    data: await parseJson(response),
  };
}
